import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Trip } from '../../types/client';
import { tripsEn } from '../../content/trips.en';
import { tripShareEn } from '../../content/tripShare.en';
import { useAuth } from '../../contexts/AuthContext';
import { isSharedTrip } from '../../utils/tripAccessPolicy';
import { tripWebHref } from '../../services/tripsService';
import { buildTripStaticMapUrl, markersFromTripCoords } from '../../utils/tripStaticMap';
import { formatTripHeaderDate, pendingTaskCount } from '../../utils/tripDayCards';
import styles from './TripListCard.module.css';

interface TripListCardProps {
  trip: Trip;
}

function formatTripDate(iso: string | null): string | null {
  return formatTripHeaderDate(iso);
}

const TripListCard: React.FC<TripListCardProps> = ({ trip }) => {
  const { user } = useAuth();
  const title = trip.title?.trim() || tripsEn.untitledTrip;
  const cityCount = trip.cityIds?.length ?? 0;
  const pending = pendingTaskCount(trip);
  const shared = Boolean(user?.sub && isSharedTrip(trip, user.sub));
  const startLabel = formatTripDate(trip.startDate);
  const endLabel = formatTripDate(trip.endDate);
  const dateRange =
    startLabel && endLabel
      ? `${startLabel} - ${endLabel}`
      : startLabel || endLabel || null;

  const markers = markersFromTripCoords(trip.cityIds ?? [], trip.cityCoords);
  const mapUrl = buildTripStaticMapUrl(markers, {
    drawPath: trip.tripType !== 'cruise' && markers.length > 1,
  });
  const [mapFailed, setMapFailed] = useState(false);

  return (
    <Link to={user?.sub ? tripWebHref(user.sub, trip) : `/trips/${trip.id}`} className={styles.card}>
      <div className={styles.thumbnail}>
        {mapUrl && !mapFailed ? (
          <img
            src={mapUrl}
            alt=""
            className={styles.mapImage}
            loading="lazy"
            decoding="async"
            onError={() => setMapFailed(true)}
          />
        ) : (
          <div className={styles.mapFallback} aria-hidden>
            <div className={styles.fallbackPins}>
              {(trip.cityIds ?? []).slice(0, 8).map((id, index) => (
                <span
                  key={id}
                  className={`${styles.fallbackPin} ${
                    index === 0 || index === Math.min((trip.cityIds?.length ?? 1) - 1, 7)
                      ? styles.fallbackPinEnd
                      : ''
                  }`}
                  style={{
                    left: `${12 + ((index * 37) % 70)}%`,
                    top: `${18 + ((index * 29) % 55)}%`,
                  }}
                >
                  {index < 26 ? String.fromCharCode(65 + index) : index + 1}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={styles.details}>
        <div className={styles.titleRow}>
          <h2 className={styles.title}>{title}</h2>
          {shared ? <span className={styles.badge}>{tripShareEn.sharedBadge}</span> : null}
          {pending > 0 ? <span className={styles.badge}>{pending}</span> : null}
        </div>
        {dateRange ? <div className={styles.meta}>{dateRange}</div> : null}
        {cityCount > 0 ? (
          <div className={styles.meta}>{tripsEn.cityCount(cityCount)}</div>
        ) : null}
      </div>

      <span className={styles.chevron} aria-hidden>
        ›
      </span>
    </Link>
  );
};

export default TripListCard;
